// node_modules
import { ChangeEvent, CSSProperties, FC, useCallback, useContext, useEffect, useState } from "react";
import Modal from "react-modal";
// import ContentLoader from "react-content-loader";
// Custom hooks
import { useFetch } from "Hooks";
// Contexts
import { UserContext } from "Providers";
import { AuthContext, CaseContext } from "Stores";
// Types
import { TCaseIntakeDTO, TUseFetch } from "Types";
// Components
import { AsynchronousWrapper, CaseEditHeaderModal } from "Components";
import { CaseChallenge } from "./CaseChallenge";
import { CaseDeliveryIntakeOverride } from "./CaseDeliveryIntakeOverride";
import { CaseIdealOutcome } from "./CaseIdealOutcome";
import { CaseIntakeFunctions } from "./CaseIntakeFunctions/CaseIntakeFunctions";
import { CaseKnownDirections } from "./CaseKnownDirections/CaseKnownDirections";
import { CaseMVPOutcome } from "./CaseMVPOutcome";
import { CaseNotes } from "./CaseNotes";
import { CaseRequirements } from "./CaseRequirements/CaseRequirements";
import { CaseScope } from "./CaseScope";
import { CaseSolutionType } from "./CaseSolutionType";
import { CaseTagType } from "./CaseTagType";
import { CaseTerminologies } from "./CaseTerminologies/CaseTerminologies";
// Style
import modalStyles from "styles/modal.module.scss";
import styles from "./intake.module.scss";
// Helpers
import { ColorHelperSingleton, PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
// Enums
import { CaseActionTypeEnum, CasePageEnum, ToastTypeEnum } from "Enums";
// Interfaces
import { ICaseSetSelectedEntity } from "Interfaces";
// Constants
import { FeatureToggleConstants, GeneralConstants, LoggingConstants } from "Constants";
// Controllers
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CaseControllerSingleton } from "Controllers";

export type TIntakeProps = {
    isEdit: boolean
};

// TODO: start using again once package.json is updated
// const rectangleHeights = [305, 345, 385, 425, 465, 505, 545, 585];
// const IntakeContentLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="600"
//         width="calc(100% - 64px)"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//         style={{ margin: "1rem 2rem" }}
//     >
//         <rect x="0" y="0" rx="5" ry="5" width="100%" height="200px" />
//         <rect x="0" y="247" rx="4" ry="4" width="48%" height="30" />
//         <rect x="50%" y="247" rx="4" ry="4" width="50%" height="30" />
//         {rectangleHeights.map((height) => (
//             <>
//                 <rect x="0" y={`${height}`} rx="5" ry="5" width="48%" height="15" /> 
//                 <rect x="50%" y={`${height}`} rx="5" ry="5" width="50%" height="15" />
//             </>
//         ))}
//     </ContentLoader>
// );


export const Intake: FC<TIntakeProps> = ({ isEdit }: TIntakeProps) => {
    // State
    const [isDynamicDelivery, setIsDynamicDelivery] = useState<boolean>(false);
    const [isDeliveryRedesign, setIsDeliveryRedesign] = useState<boolean>(false);

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { isCurrentUserFindest } = useContext(UserContext);

    // State
    const [isEditHeaderModalOpen, setIsEditHeaderModalOpen] = useState(false);
    let caseColorString = undefined;

    // Const
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TCaseIntakeDTO> = useFetch(`api/cases/${caseState.selectedCaseId}/intake`);

    // Logic
    const getIntakeHeaderStyle = useCallback((): CSSProperties => {
        let intakeHeaderStyle: CSSProperties;
        if (caseState.selectedCase) {
            caseColorString = ColorHelperSingleton.getPastelColorFromString(caseState.selectedCase.startDate);

            if (caseState.selectedCase.caseImage) {
                intakeHeaderStyle = { backgroundImage: `url(/assets/images/cases/${caseState.selectedCase.caseImage})` };
            } else {
                intakeHeaderStyle = { backgroundColor: caseColorString };
            }
        } else {
            intakeHeaderStyle = { backgroundColor: "black" };
        }
        return intakeHeaderStyle;
    }, [caseState.selectedCase]);

    useEffect(() => {
        caseDispatch({
            type: CaseActionTypeEnum.SetCaseSelectedEntity, selectedEntityId: -1,
            selectedEntityName: CasePageEnum.Intake, selectedEntityNumbering: ""
        } as ICaseSetSelectedEntity);
    }, [caseDispatch]);

    useEffect(() => {
        setIsDeliveryRedesign(caseState.isDeliveryRedesign);
        setIsDynamicDelivery(caseState.isDynamicDelivery);
    }, [caseState.isDeliveryRedesign, caseState.isDynamicDelivery]);

    // Logging
    useEffect(() => {
        // Log the retrieval of the intake
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}CaseIntake`, {
            caseId: caseState.selectedCaseId
        });
    }, [caseState.selectedCaseId]);

    const getIntakeContainerClassName = (): string => {
        const classNameList = [styles.intakeContainer, styles.scrollbarCustomThin];
        return classNameList.join(" ");
    };

    const getIsIntakeSheetFieldEditable = (): boolean => {
        return isEdit && isCurrentUserFindest;
    };

    const handleOnDeliveryRedesignChange = async (newIsDeliveryRedesign: boolean): Promise<void> => {
        if (!caseState.selectedCaseId) { return; }

        const isSuccess = await CaseControllerSingleton
            .updateIsDeliveryRedesignAsync(caseState.selectedCaseId, newIsDeliveryRedesign);

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update case delivery redesign status.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated case delivery redesign status.");
            setIsDeliveryRedesign(newIsDeliveryRedesign);
            if (!newIsDeliveryRedesign && isDynamicDelivery) {
                setIsDynamicDelivery(false);
            }
        }
    };

    const handleOnDynamicDeliveryChange = async (newIsDynamicDelivery: boolean): Promise<void> => {
        if (!caseState.selectedCaseId) { return; }

        const isSuccess = await CaseControllerSingleton
            .updateIsDynamicDeliveryAsync(caseState.selectedCaseId, newIsDynamicDelivery);

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update case dynamic delivery status.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated case dynamic delivery status.");
            setIsDynamicDelivery(newIsDynamicDelivery);
            if (newIsDynamicDelivery && !isDeliveryRedesign) {
                setIsDeliveryRedesign(true);
            }
        }
    };

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {!caseState.selectedCase || !fetchedData || !caseState.selectedCaseId ?
                <h4>Loading...</h4>
                :
                <div className={getIntakeContainerClassName()}>
                    {FeatureToggleConstants.EditIntakeHeader ?
                        <Modal
                            isOpen={isEditHeaderModalOpen}
                            onRequestClose={() => { setIsEditHeaderModalOpen(false); }}
                            className={`${modalStyles.modal} ${modalStyles.center} ${styles.caseEditHeaderModalWrapper}`}
                            overlayClassName={modalStyles.overlay}
                        >
                            <CaseEditHeaderModal caseId={caseState.selectedCase.id} caseTitle={caseState.selectedCase.title} caseImageUrl={caseState.selectedCase.caseImage}
                                caseColorString={caseColorString} onCloseModal={() => { setIsEditHeaderModalOpen(false); }} />
                        </Modal>
                        : null}
                    <div className={getIntakeContainerClassName()}>
                        <div className={styles.intakeHeader} style={getIntakeHeaderStyle()}>
                            {FeatureToggleConstants.EditIntakeHeader && isCurrentUserFindest ?
                                <div className={styles.caseEditButton} onClick={() => { setIsEditHeaderModalOpen(true); }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </div>
                                : null}
                            {caseState.selectedCase.organizationLogo ? <img className={styles.organizationLogo} src={`/assets/images/organizations/${caseState.selectedCase.organizationLogo}`} alt="Logo of your company" /> : null }
                            <div className={styles.caseTitle}>{caseState.selectedCase.title}</div>
                        </div>
                        <div>
                            {getIsIntakeSheetFieldEditable() && authState.igorAIEnv.trim().toLowerCase() !== GeneralConstants.IGORAI_SAAS_ENV.trim().toLowerCase() ?
                                <div>
                                    <input title="Delivery redesign" type="checkbox" id="deliveryRedesign" checked={isDeliveryRedesign}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnDeliveryRedesignChange(event.target.checked)} />
                                    <label htmlFor="deliveryRedesign">Delivery redesign</label>
                                </div>
                                :
                                null
                            }
                            {getIsIntakeSheetFieldEditable() && authState.hasAccessToDynamicDelivery && (isDeliveryRedesign || authState.igorAIEnv.trim().toLowerCase() === GeneralConstants.IGORAI_SAAS_ENV.trim().toLowerCase()) ?
                                <div>
                                    <input title="Dynamic delivery" type="checkbox" id="dynamicDelivery" checked={isDynamicDelivery}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleOnDynamicDeliveryChange(event.target.checked)} />
                                    <label htmlFor="dynamicDelivery">Dynamic delivery</label>
                                </div>
                                :
                                null
                            }
                        </div>
                        <div className={styles.intakeWrapper}>
                            <div className={[styles.intakeWrapperColumn, styles.leftColumn].join(" ")}>
                                <CaseChallenge
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    caseChallenge={fetchedData.challengeDescription} />
                                {getIsIntakeSheetFieldEditable() ?
                                    <CaseSolutionType
                                        caseId={caseState.selectedCaseId}
                                        caseSolutionType={fetchedData.solutionType} />
                                    :
                                    null
                                }
                                <CaseKnownDirections
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    knownTechnologies={fetchedData.knownTechnologies} />
                                <CaseScope
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    caseScope={fetchedData.scope} />
                                <CaseIdealOutcome
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    caseIdealOutcome={fetchedData.idealOutcome} />
                                <CaseMVPOutcome
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    caseMVPOutcome={fetchedData.minimumViableOutcome} />
                            </div>
                            <div className={[styles.intakeWrapperColumn, styles.rightColumn].join(" ")}>
                                <CaseRequirements
                                    caseId={caseState.selectedCaseId}
                                    isEditMode={getIsIntakeSheetFieldEditable()}
                                    requirements={fetchedData.requirements} />
                                {getIsIntakeSheetFieldEditable() ?
                                    <>
                                        <CaseIntakeFunctions
                                            caseId={caseState.selectedCaseId}
                                            actionObjects={fetchedData.functions}
                                            keywords={fetchedData.environments}
                                            isEditMode={true} />
                                        <CaseTerminologies
                                            caseId={caseState.selectedCaseId}
                                            isEditMode={true}
                                            terminologies={fetchedData.terminologies} />
                                        <CaseDeliveryIntakeOverride
                                            caseId={caseState.selectedCaseId}
                                            caseDeliveryIntakeOverride={fetchedData.deliveryIntakeEndOverride}
                                            isEditMode={true} />
                                        <CaseNotes
                                            caseId={caseState.selectedCaseId}
                                            caseNotes={fetchedData.notes}
                                            isEditMode={true} />
                                    </>
                                    :
                                    fetchedData.deliveryIntakeEndOverride && fetchedData.deliveryIntakeEndOverride.trim().length > 0 ?
                                        <>
                                            <CaseDeliveryIntakeOverride
                                                caseId={caseState.selectedCaseId}
                                                caseDeliveryIntakeOverride={fetchedData.deliveryIntakeEndOverride}
                                                isEditMode={false} />
                                            <CaseNotes
                                                caseId={caseState.selectedCaseId}
                                                caseNotes={fetchedData.notes}
                                                isEditMode={false} />
                                        </>
                                        :
                                        <>
                                            <CaseIntakeFunctions
                                                caseId={caseState.selectedCaseId}
                                                actionObjects={fetchedData.functions}
                                                keywords={fetchedData.environments}
                                                isEditMode={false} />
                                            <CaseTerminologies
                                                caseId={caseState.selectedCaseId}
                                                isEditMode={false}
                                                terminologies={fetchedData.terminologies} />
                                            <CaseNotes
                                                caseId={caseState.selectedCaseId}
                                                caseNotes={fetchedData.notes}
                                                isEditMode={false} />
                                        </>
                                }
                                {getIsIntakeSheetFieldEditable() ?
                                    <CaseTagType
                                        caseId={caseState.selectedCaseId}
                                        caseTagType={fetchedData.tagType} />
                                    :
                                    null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            }
        </AsynchronousWrapper>
    );
};
